/**
 * teacher — client feature module (spec §4.1, §4.9, brief §teacher).
 * `requiresRole: "teacher"` guards every route of the feature in the router
 * and hides its nav entries for students.
 *
 * A single top-level path (`/teacher`) with nested children — not several
 * top-level routes — so this feature still contributes exactly one entry to
 * `client/src/app/router.test.tsx`'s "matches every route every feature
 * registers" check (frozen: one path per feature), the same pattern
 * `practice/index.ts`, `exam/index.ts` and `flashcards/index.ts` use.
 *
 * `"teacher.nav"` (sv: "Lärarvy") is asserted verbatim by that same frozen
 * router test ("lets a teacher into the teacher feature") — do not reword it.
 */
import { Users } from "lucide-react";
import type { FeatureModule } from "@client/app/types";
import { TeacherExamsPage } from "./ExamsPage";
import { TeacherPage } from "./Page";
import { TeacherPrepIndexPage } from "./prep/PrepIndexPage";
import { TeacherPrepWorkbenchPage } from "./prep/WorkbenchPage";
import { TeacherQuestionsPage } from "./QuestionsPage";
import { TeacherReleasesPage } from "./ReleasesPage";
import { TeacherReportsPage } from "./ReportsPage";
import { TeacherStudentDetailPage } from "./StudentDetailPage";
import { TeacherStudentsPage } from "./StudentsPage";

import { dict as relativityDict } from "./prep/models/relativity/dict";
import { dict as photonsDict } from "./prep/models/photons/dict";
import { dict as spacetimeDict } from "./prep/models/spacetime/dict";

const teacherFeature: FeatureModule = {
  id: "teacher",
  requiresRole: "teacher",
  routes: [
    {
      path: "/teacher",
      children: [
        { index: true, Component: TeacherPage },
        { path: "students", Component: TeacherStudentsPage },
        { path: "students/:id", Component: TeacherStudentDetailPage },
        { path: "questions", Component: TeacherQuestionsPage },
        { path: "exams", Component: TeacherExamsPage },
        { path: "reports", Component: TeacherReportsPage },
        { path: "prep", Component: TeacherPrepIndexPage },
        { path: "prep/:lectureId", Component: TeacherPrepWorkbenchPage },
        { path: "releases", Component: TeacherReleasesPage },
      ],
    },
  ],
  nav: [
    {
      to: "/teacher",
      labelKey: "teacher.nav",
      icon: Users,
      order: 60,
      requiresRole: "teacher",
      hideDuringExam: true,
    },
  ],
  dict: {
    sv: {
      ...spacetimeDict.sv,
      ...relativityDict.sv,
      ...photonsDict.sv,
      "teacher.nav": "Lärarvy",
      "teacher.chapterLabel": "Moment",
      "teacher.export.cta": "Exportera CSV",
      "teacher.export.progress.one": "Exporterar … {count} rad",
      "teacher.export.progress.other": "Exporterar … {count} rader",
      "teacher.role.student": "Elev",
      "teacher.role.teacher": "Lärare",
      "teacher.mode.practice": "Övning",
      "teacher.mode.exam": "Övningstenta",
      "teacher.gradedBy.auto": "Automatiskt rättat",
      "teacher.gradedBy.self": "Självrättat",
      "teacher.questionType.mc": "Flerval",
      "teacher.questionType.numeric": "Numerisk",
      "teacher.questionType.open": "Öppen",
      "teacher.examKind.generated": "Slumpad övningstenta",
      "teacher.examStatus.in_progress": "Pågår",
      "teacher.examStatus.submitted": "Inlämnad",
      "teacher.examStatus.graded": "Rättad",
      "teacher.examStatus.expired": "Tiden ute",
      "teacher.examStatus.abandoned": "Avbruten",

      "teacher.tabs.overview": "Översikt",
      "teacher.tabs.students": "Elever",
      "teacher.tabs.questions": "Frågor",
      "teacher.tabs.exams": "Övningstentor",
      "teacher.tabs.reports": "Rapporter",
      "teacher.tabs.prep": "Förberedelse",
      "teacher.tabs.releases": "Publicering",
      "teacher.releases.title": "Publicering",
      "teacher.releases.description":
        "Bestäm när eleverna kan öva på frågorna till varje föreläsning. Lärare ser alltid alla frågor.",
      "teacher.releases.offset.label": "Frågorna öppnas före föreläsningen",
      "teacher.releases.offset.hint":
        "Gäller varje tillfälle utan eget val. Öppnar kl. 00:00. Standard: {days} dagar.",
      "teacher.releases.offset.suffix": "dagar före",
      "teacher.releases.save": "Spara",
      "teacher.releases.saved": "Publiceringen är sparad.",
      "teacher.releases.error": "Publiceringen kunde inte läsas.",
      "teacher.releases.col.lecture": "Tillfälle",
      "teacher.releases.col.date": "Datum",
      "teacher.releases.col.opens": "Öppnas",
      "teacher.releases.col.state": "Läge",
      "teacher.releases.col.change": "Ändra",
      "teacher.releases.state.open": "Öppen",
      "teacher.releases.state.closed": "Stängd",
      "teacher.releases.opens.open": "Öppnad för hand",
      "teacher.releases.opens.closed": "Stängd tills vidare",
      "teacher.releases.mode.default": "Standard ({days} dagar före)",
      "teacher.releases.mode.date": "Eget datum",
      "teacher.releases.mode.open": "Öppna nu",
      "teacher.releases.mode.closed": "Stängd tills vidare",
      "teacher.releases.modeLabel": "Publicering för {id}",
      "teacher.releases.dateLabel": "Öppningsdatum för {id}",
      "teacher.releases.saveLabel": "Spara {id}",

      "teacher.overview.description":
        "Klassens aktivitet, träffsäkerhet och resultat på övningstentor, uppdelat på automatiskt och självrättat.",
      "teacher.overview.stats.students.label": "Elever",
      "teacher.overview.stats.students.hint": "{active7d} aktiva 7 d · {active30d} aktiva 30 d",
      "teacher.overview.stats.attempts.label": "Besvarade frågor",
      "teacher.overview.stats.attempts.hint": "{count} senaste 7 dagarna",
      "teacher.overview.stats.exams.label": "Rättade övningstentor",
      "teacher.overview.stats.exams.hint": "Snitt {score}",
      "teacher.overview.stats.verdicts.label": "Betygsfördelning",
      "teacher.overview.chart.title": "Träffsäkerhet per moment",
      "teacher.overview.chart.auto": "automatiskt rättat",
      "teacher.overview.chart.self": "självrättat",
      "teacher.overview.chart.empty": "Ingen aktivitet ännu.",
      "teacher.onboarding.title": "Kom igång-svaren",
      "teacher.onboarding.summary":
        "{answered} har svarat · {skipped} hoppade över · {notYet} inte än",
      "teacher.onboarding.privacy":
        "Bara sammanräknade antal för eleverna. Svaren visas aldrig per elev.",
      "teacher.onboarding.tooFew": "Fördelningen visas när minst {min} elever har svarat.",
      "teacher.onboarding.position": "Var i kursen",
      "teacher.onboarding.goal": "Siktar på",
      "teacher.onboarding.minutes": "Tid per dag",
      "teacher.onboarding.math": "Matematiken",

      "teacher.students.title": "Elever",
      "teacher.students.description":
        "Sök och sortera klassens elever; klicka på en elev för detaljer och rollhantering.",
      "teacher.students.search.label": "Sök",
      "teacher.students.search.placeholder": "Namn eller e-post",
      "teacher.students.roleFilter.label": "Visa",
      "teacher.students.roleFilter.student": "Elever",
      "teacher.students.roleFilter.teacher": "Lärare",
      "teacher.students.roleFilter.all": "Alla",
      "teacher.students.sort.label": "Sortera",
      "teacher.students.sort.lastActive": "Senast aktiv",
      "teacher.students.sort.attempts": "Flest försök",
      "teacher.students.sort.accuracy": "Träffsäkerhet",
      "teacher.students.table.name": "Namn",
      "teacher.students.table.email": "E-post",
      "teacher.students.table.role": "Roll",
      "teacher.students.table.attempts": "Försök",
      "teacher.students.table.autoAccuracy": "Auto",
      "teacher.students.table.selfAccuracy": "Själv",
      "teacher.students.table.bestExamScore": "Bästa övningstenta",
      "teacher.students.table.lastActive": "Senast aktiv",
      "teacher.students.empty": "Inga elever ännu.",

      "teacher.detail.title": "Elevdetaljer",
      "teacher.detail.role.title": "Rollhantering",
      "teacher.detail.role.label": "Roll",
      "teacher.detail.role.apply": "Spara roll",
      "teacher.detail.role.self": "Du kan inte ändra din egen roll.",
      "teacher.detail.role.success": "Rollen har ändrats.",
      "teacher.detail.chapters.title": "Träffsäkerhet per moment",
      "teacher.detail.chapters.attempts.one": "{count} försök",
      "teacher.detail.chapters.attempts.other": "{count} försök",
      "teacher.detail.chapters.coverage": "{practised} av {total} ämnen övade",
      "teacher.detail.evidence.title": "Underlag",
      "teacher.detail.evidence.description":
        "Samma underlag som eleven ser under Framsteg: ämnen per läge och uppgifter eleven löst. Elevens egen säkerhetsskattning visas inte.",
      "teacher.detail.evidence.unaided": "Lösta på egen hand",
      "teacher.detail.evidence.assisted": "Lösta med hjälp",
      "teacher.detail.evidence.retests": "Klarade repetitionsfrågor",
      "teacher.detail.evidence.retestsValue": "{passed} av {taken}",
      "teacher.detail.evidence.statesLabel": "Ämnen per läge",
      "teacher.detail.evidence.state.not-started": "Inte påbörjat",
      "teacher.detail.evidence.state.tried": "Försökt",
      "teacher.detail.evidence.state.with-help": "Bara med hjälp",
      "teacher.detail.evidence.state.on-your-own": "På egen hand",
      "teacher.detail.evidence.state.secure": "Befäst",
      "teacher.detail.evidence.topics.title": "Påbörjade ämnen",
      "teacher.detail.evidence.topics.topic": "Ämne",
      "teacher.detail.evidence.topics.state": "Läge",
      "teacher.detail.evidence.topics.items": "Försökta",
      "teacher.detail.evidence.topics.unaided": "På egen hand",
      "teacher.detail.evidence.topics.assisted": "Med hjälp",
      "teacher.detail.evidence.topics.empty": "Inga ämnen påbörjade ännu.",
      "teacher.detail.exams.title": "Övningstentor",
      "teacher.detail.exams.empty": "Inga övningstentor genomförda ännu.",
      "teacher.detail.recentAttempts.title": "Senaste 50 försöken",
      "teacher.detail.recentAttempts.question": "Fråga",
      "teacher.detail.recentAttempts.mode": "Läge",
      "teacher.detail.recentAttempts.gradedBy": "Rättning",
      "teacher.detail.recentAttempts.score": "Poäng",
      "teacher.detail.recentAttempts.date": "Datum",
      "teacher.detail.recentAttempts.empty": "Inga försök ännu.",
      "teacher.detail.recent.assisted": "med hjälp",
      "teacher.detail.recent.retry": "andra försöket",

      "teacher.questions.title": "Frågor",
      "teacher.questions.description":
        "Svårast först, uppdelat på automatisk träffsäkerhet eller andel poäng vid självrättning.",
      "teacher.questions.filter.chapter": "Moment",
      "teacher.questions.filter.allChapters": "Alla moment",
      "teacher.questions.filter.type": "Typ",
      "teacher.questions.filter.allTypes": "Alla typer",
      "teacher.questions.search.label": "Sök",
      "teacher.questions.search.placeholder": "Fråge-id, ämne eller text",
      "teacher.questions.attempts.one": "{count} försök",
      "teacher.questions.attempts.other": "{count} försök",
      "teacher.questions.noAttempts": "inga försök",
      "teacher.questions.rubricMissRates.title": "Missade bedömningspunkter",
      "teacher.questions.empty": "Inga frågor matchar filtret.",
      "teacher.questions.count.one": "{count} fråga",
      "teacher.questions.count.other": "{count} frågor",
      "teacher.questions.pagination.previous": "Föregående",
      "teacher.questions.pagination.status": "Sida {page} av {total}",

      "teacher.exams.title": "Övningstentor",
      "teacher.exams.description":
        "Poängfördelning, betyg och de senaste genomförda övningstentorna i klassen.",
      "teacher.exams.stats.vg": "Väl godkänt",
      "teacher.exams.stats.g": "Godkänt",
      "teacher.exams.stats.u": "Underkänt",
      "teacher.exams.chart.title": "Poängfördelning",
      "teacher.exams.chart.empty": "Inga rättade övningstentor ännu.",
      "teacher.exams.chart.students": "Elever",
      "teacher.exams.byChapter.title": "Andel poäng per moment (självrättat)",
      "teacher.exams.recent.title": "Senaste övningstentorna",
      "teacher.exams.recent.student": "Elev",
      "teacher.exams.recent.kind": "Typ",
      "teacher.exams.recent.date": "Datum",
      "teacher.exams.recent.score": "Poäng",
      "teacher.exams.recent.verdict": "Betyg",
      "teacher.exams.recent.empty": "Inga övningstentor genomförda ännu.",

      "teacher.reports.title": "Rapporterade frågor",
      "teacher.reports.description": "Frågor elever har flaggat som felaktiga eller otydliga.",
      "teacher.reports.tabs.open": "Öppna",
      "teacher.reports.tabs.resolved": "Åtgärdade",
      "teacher.reports.resolve": "Markera åtgärdad",
      "teacher.reports.anonymous": "Anonym",
      "teacher.reports.empty": "Inga rapporter här.",

      "teacher.prep.title": "Förberedelse",
      "teacher.prep.description":
        "Arbetsbänkar för varje föreläsning och räkneövning — bara för dig.",
      "teacher.prep.notAvailable": "Inte förberedd än",
      "teacher.prep.model.error": "Modellen kunde inte laddas. Ladda om sidan.",
      "teacher.prep.draft": "Ej granskad",
      "teacher.prep.kind.lecture": "Föreläsning",
      "teacher.prep.kind.problem-session": "Räkneövning",
      "teacher.prep.back": "Alla föreläsningar",
      "teacher.prep.panel.model": "Modell",
      "teacher.prep.panel.derivation": "Härledning",
      "teacher.prep.panel.interpretations": "Tolkningar",
      "teacher.prep.panel.history": "Historisk båge",
      "teacher.prep.panel.problems": "Problem att ha till hands",
      "teacher.prep.panel.recaps": "Repetition",
      "teacher.prep.panel.book": "I boken",
      "teacher.prep.show": "Visa",
      "teacher.prep.hide": "Dölj",
      "teacher.prep.showInModel": "Visa i modellen",
      "teacher.prep.loadProblem": "Ladda i modellen",
      "teacher.prep.solution": "Visa genomarbetad lösning",
      "teacher.prep.pitfall": "Vanligt fel:",
      "teacher.prep.misreadings": "Vanliga men missvisande läsningar",
      "teacher.prep.attempt": "Ditt resonemang …",
      "teacher.prep.reset": "Återställ modellen",
      "teacher.prep.model.empty":
        "Välj ett steg, ett problem eller en epok för att ladda modellen.",
      "teacher.prep.limits": "Modellens gränser",
      "teacher.prep.variants": "Varianter",
      "teacher.prep.answer": "Svar:",
      "teacher.prep.derivedIn": "Härleds i {id}",
      "teacher.prep.calibrated": "Kalibrerat mot:",
    },
    en: {
      ...spacetimeDict.en,
      ...relativityDict.en,
      ...photonsDict.en,
      "teacher.nav": "Teacher",
      "teacher.chapterLabel": "Module",
      "teacher.export.cta": "Export CSV",
      "teacher.export.progress.one": "Exporting … {count} row",
      "teacher.export.progress.other": "Exporting … {count} rows",
      "teacher.role.student": "Student",
      "teacher.role.teacher": "Teacher",
      "teacher.mode.practice": "Practice",
      "teacher.mode.exam": "Exam",
      "teacher.gradedBy.auto": "Auto-graded",
      "teacher.gradedBy.self": "Self-graded",
      "teacher.questionType.mc": "Multiple choice",
      "teacher.questionType.numeric": "Numeric",
      "teacher.questionType.open": "Open",
      "teacher.examKind.generated": "Generated exam",
      "teacher.examStatus.in_progress": "In progress",
      "teacher.examStatus.submitted": "Submitted",
      "teacher.examStatus.graded": "Graded",
      "teacher.examStatus.expired": "Time's up",
      "teacher.examStatus.abandoned": "Abandoned",

      "teacher.tabs.overview": "Overview",
      "teacher.tabs.students": "Students",
      "teacher.tabs.questions": "Questions",
      "teacher.tabs.exams": "Exams",
      "teacher.tabs.reports": "Reports",
      "teacher.tabs.prep": "Preparation",
      "teacher.tabs.releases": "Release",
      "teacher.releases.title": "Release",
      "teacher.releases.description":
        "Decide when students can practise each lecture's questions. Teachers always see every question.",
      "teacher.releases.offset.label": "Questions open before the lecture",
      "teacher.releases.offset.hint":
        "Applies to every session without its own choice. Opens at 00:00. Default: {days} days.",
      "teacher.releases.offset.suffix": "days before",
      "teacher.releases.save": "Save",
      "teacher.releases.saved": "Release saved.",
      "teacher.releases.error": "The release settings could not be loaded.",
      "teacher.releases.col.lecture": "Session",
      "teacher.releases.col.date": "Date",
      "teacher.releases.col.opens": "Opens",
      "teacher.releases.col.state": "State",
      "teacher.releases.col.change": "Change",
      "teacher.releases.state.open": "Open",
      "teacher.releases.state.closed": "Closed",
      "teacher.releases.opens.open": "Opened by hand",
      "teacher.releases.opens.closed": "Closed until further notice",
      "teacher.releases.mode.default": "Default ({days} days before)",
      "teacher.releases.mode.date": "Own date",
      "teacher.releases.mode.open": "Open now",
      "teacher.releases.mode.closed": "Closed until further notice",
      "teacher.releases.modeLabel": "Release of {id}",
      "teacher.releases.dateLabel": "Opening date of {id}",
      "teacher.releases.saveLabel": "Save {id}",

      "teacher.overview.description":
        "Class activity, accuracy and exam results, split by auto- and self-grading.",
      "teacher.overview.stats.students.label": "Students",
      "teacher.overview.stats.students.hint": "{active7d} active 7d · {active30d} active 30d",
      "teacher.overview.stats.attempts.label": "Questions answered",
      "teacher.overview.stats.attempts.hint": "{count} in the last 7 days",
      "teacher.overview.stats.exams.label": "Graded exams",
      "teacher.overview.stats.exams.hint": "Average {score}",
      "teacher.overview.stats.verdicts.label": "Verdict split",
      "teacher.overview.chart.title": "Accuracy by module",
      "teacher.overview.chart.auto": "auto-graded",
      "teacher.overview.chart.self": "self-graded",
      "teacher.overview.chart.empty": "No activity yet.",
      "teacher.onboarding.title": "Kom igång answers",
      "teacher.onboarding.summary": "{answered} answered · {skipped} skipped · {notYet} not yet",
      "teacher.onboarding.privacy":
        "Totals for the students only. Answers are never shown per student.",
      "teacher.onboarding.tooFew":
        "The breakdown appears once at least {min} students have answered.",
      "teacher.onboarding.position": "Where in the course",
      "teacher.onboarding.goal": "Aiming for",
      "teacher.onboarding.minutes": "Time per day",
      "teacher.onboarding.math": "The maths",

      "teacher.students.title": "Students",
      "teacher.students.description":
        "Search and sort the class roster; click a student for details and role management.",
      "teacher.students.search.label": "Search",
      "teacher.students.search.placeholder": "Name or email",
      "teacher.students.roleFilter.label": "Show",
      "teacher.students.roleFilter.student": "Students",
      "teacher.students.roleFilter.teacher": "Teachers",
      "teacher.students.roleFilter.all": "All",
      "teacher.students.sort.label": "Sort",
      "teacher.students.sort.lastActive": "Last active",
      "teacher.students.sort.attempts": "Most attempts",
      "teacher.students.sort.accuracy": "Accuracy",
      "teacher.students.table.name": "Name",
      "teacher.students.table.email": "Email",
      "teacher.students.table.role": "Role",
      "teacher.students.table.attempts": "Attempts",
      "teacher.students.table.autoAccuracy": "Auto",
      "teacher.students.table.selfAccuracy": "Self",
      "teacher.students.table.bestExamScore": "Best exam",
      "teacher.students.table.lastActive": "Last active",
      "teacher.students.empty": "No students yet.",

      "teacher.detail.title": "Student detail",
      "teacher.detail.role.title": "Role management",
      "teacher.detail.role.label": "Role",
      "teacher.detail.role.apply": "Save role",
      "teacher.detail.role.self": "You cannot change your own role.",
      "teacher.detail.role.success": "Role changed.",
      "teacher.detail.chapters.title": "Accuracy by module",
      "teacher.detail.chapters.attempts.one": "{count} attempt",
      "teacher.detail.chapters.attempts.other": "{count} attempts",
      "teacher.detail.chapters.coverage": "{practised} of {total} topics practised",
      "teacher.detail.evidence.title": "Evidence",
      "teacher.detail.evidence.description":
        "The same evidence the student sees under Progress: topics by state and the problems they have solved. The student's own confidence ratings are not shown.",
      "teacher.detail.evidence.unaided": "Solved on their own",
      "teacher.detail.evidence.assisted": "Solved with help",
      "teacher.detail.evidence.retests": "Re-tests passed",
      "teacher.detail.evidence.retestsValue": "{passed} of {taken}",
      "teacher.detail.evidence.statesLabel": "Topics by state",
      "teacher.detail.evidence.state.not-started": "Not started",
      "teacher.detail.evidence.state.tried": "Tried",
      "teacher.detail.evidence.state.with-help": "With help only",
      "teacher.detail.evidence.state.on-your-own": "On their own",
      "teacher.detail.evidence.state.secure": "Secure",
      "teacher.detail.evidence.topics.title": "Topics started",
      "teacher.detail.evidence.topics.topic": "Topic",
      "teacher.detail.evidence.topics.state": "State",
      "teacher.detail.evidence.topics.items": "Tried",
      "teacher.detail.evidence.topics.unaided": "On their own",
      "teacher.detail.evidence.topics.assisted": "With help",
      "teacher.detail.evidence.topics.empty": "No topics started yet.",
      "teacher.detail.exams.title": "Exam history",
      "teacher.detail.exams.empty": "No exams completed yet.",
      "teacher.detail.recentAttempts.title": "50 most recent attempts",
      "teacher.detail.recentAttempts.question": "Question",
      "teacher.detail.recentAttempts.mode": "Mode",
      "teacher.detail.recentAttempts.gradedBy": "Grading",
      "teacher.detail.recentAttempts.score": "Score",
      "teacher.detail.recentAttempts.date": "Date",
      "teacher.detail.recentAttempts.empty": "No attempts yet.",
      "teacher.detail.recent.assisted": "with help",
      "teacher.detail.recent.retry": "second try",

      "teacher.questions.title": "Questions",
      "teacher.questions.description":
        "Hardest first, split by auto-graded accuracy or self-graded score share.",
      "teacher.questions.filter.chapter": "Module",
      "teacher.questions.filter.allChapters": "All modules",
      "teacher.questions.filter.type": "Type",
      "teacher.questions.filter.allTypes": "All types",
      "teacher.questions.search.label": "Search",
      "teacher.questions.search.placeholder": "Question ID, topic or text",
      "teacher.questions.attempts.one": "{count} attempt",
      "teacher.questions.attempts.other": "{count} attempts",
      "teacher.questions.noAttempts": "no attempts",
      "teacher.questions.rubricMissRates.title": "Missed rubric items",
      "teacher.questions.empty": "No questions match the filter.",
      "teacher.questions.count.one": "{count} question",
      "teacher.questions.count.other": "{count} questions",
      "teacher.questions.pagination.previous": "Previous",
      "teacher.questions.pagination.status": "Page {page} of {total}",

      "teacher.exams.title": "Exams",
      "teacher.exams.description":
        "Score distribution, verdicts and the class's most recent exams.",
      "teacher.exams.stats.vg": "Pass with distinction",
      "teacher.exams.stats.g": "Pass",
      "teacher.exams.stats.u": "Fail",
      "teacher.exams.chart.title": "Score distribution",
      "teacher.exams.chart.empty": "No graded exams yet.",
      "teacher.exams.chart.students": "Students",
      "teacher.exams.byChapter.title": "Score share by module (self-graded)",
      "teacher.exams.recent.title": "Most recent exams",
      "teacher.exams.recent.student": "Student",
      "teacher.exams.recent.kind": "Kind",
      "teacher.exams.recent.date": "Date",
      "teacher.exams.recent.score": "Score",
      "teacher.exams.recent.verdict": "Verdict",
      "teacher.exams.recent.empty": "No exams completed yet.",

      "teacher.reports.title": "Reported questions",
      "teacher.reports.description": "Questions students have flagged as wrong or unclear.",
      "teacher.reports.tabs.open": "Open",
      "teacher.reports.tabs.resolved": "Resolved",
      "teacher.reports.resolve": "Mark resolved",
      "teacher.reports.anonymous": "Anonymous",
      "teacher.reports.empty": "No reports here.",

      "teacher.prep.title": "Preparation",
      "teacher.prep.description":
        "A workbench for each lecture and problem session — teacher only.",
      "teacher.prep.notAvailable": "Not prepared yet",
      "teacher.prep.model.error": "The model could not be loaded. Reload the page.",
      "teacher.prep.draft": "Not reviewed",
      "teacher.prep.kind.lecture": "Lecture",
      "teacher.prep.kind.problem-session": "Problem session",
      "teacher.prep.back": "All lectures",
      "teacher.prep.panel.model": "Model",
      "teacher.prep.panel.derivation": "Derivation",
      "teacher.prep.panel.interpretations": "Interpretations",
      "teacher.prep.panel.history": "Historical arc",
      "teacher.prep.panel.problems": "Problems to have at hand",
      "teacher.prep.panel.recaps": "Recap",
      "teacher.prep.panel.book": "In the book",
      "teacher.prep.show": "Show",
      "teacher.prep.hide": "Hide",
      "teacher.prep.showInModel": "Show in the model",
      "teacher.prep.loadProblem": "Load in the model",
      "teacher.prep.solution": "Show worked solution",
      "teacher.prep.pitfall": "Common mistake:",
      "teacher.prep.misreadings": "Common but misleading readings",
      "teacher.prep.attempt": "Your reasoning …",
      "teacher.prep.reset": "Reset the model",
      "teacher.prep.model.empty": "Pick a step, a problem or an era to load the model.",
      "teacher.prep.limits": "Limits of the model",
      "teacher.prep.variants": "Variants",
      "teacher.prep.answer": "Answer:",
      "teacher.prep.derivedIn": "Derived in {id}",
      "teacher.prep.calibrated": "Calibrated against:",
    },
  },
};

export default teacherFeature;
